import React, { useState, useContext } from 'react';
import axios from 'axios';
import moment from 'moment';

import { DataContext } from '../../../../stores/covering-table-store';
import { DataContext as MainDataContext } from '../../../../store';

import WeekHelper from '../../../../functions/WeekHelper';

import AlertModal from './sub_components/AlertModal';
import CoveringTypes from './sub_components/CoveringTypes';
import DeleteModal from './sub_components/DeleteModal';
import Description from './sub_components/Description';
import Header from './sub_components/Header';
import Percents from './sub_components/Percents';
import Submits from './sub_components/Submits';
import WeekPickers from './sub_components/WeekPickers';
import {
    getCoveringRateWithoutInitialAssignment,
    calculateDateBoundaries,
    calculateMaxPctAvailable,
    assignmentsEquals,
} from './helpMethods';

const AssignmentEditor = ({
    row,
    initialAssignment,
    dropdownRef,
    handleClose,
    createStartWeek = null,
    isAlertModalOpen,
    setIsAlertModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
}) => {
    /** Naming convention
     *  - (...)Week: string on format yyyyww
     *  - (...)DateString: string on format yyyy-ww-dd
     *  - (...)Date: javascript Date() object
     */

    // ------- Localtime -------
    const currentWeek = WeekHelper.GetWeek(new Date());

    // ------- Destructure row -------
    const { empID, empName, coveringRate } = row;
    const coveringRateWithoutInitialAssignment = getCoveringRateWithoutInitialAssignment(coveringRate, initialAssignment);
    const creatingNewAssignment = !initialAssignment || initialAssignment === null;

    // ------- DataContext -------
    const { store, dispatch } = useContext(DataContext);
    const {
        startWeek: filterStartDate,
        endWeek: filterEndDate,
        selectedDate: selectedCoveringDate,
        latestDate: latestCoveringDate,
        coverings: initialCoverings,
        bltIDToNames,
    } = store;
    const filterStartWeek = WeekHelper.GetWeek(filterStartDate);
    const filterEndWeek = WeekHelper.GetWeek(filterEndDate);
    const selectedCoveringDateString = moment(selectedCoveringDate).format('YYYY-MM-DD').toString();
    const {
        store: { user },
    } = useContext(MainDataContext);

    // ------- States -------
    const [begDate, _setBegDate] = useState(
        creatingNewAssignment ? new moment(createStartWeek, 'YYYYWW').toDate() : new moment(initialAssignment.begWek, 'YYYYWW').toDate()
    );
    const [endDate, _setEndDate] = useState(
        creatingNewAssignment ? new moment(createStartWeek, 'YYYYWW').toDate() : new moment(initialAssignment.endWek, 'YYYYWW').toDate()
    );
    const [bltID, setBltID] = useState(creatingNewAssignment ? 'UP' : initialAssignment.bltID);
    const [pct, setPct] = useState(creatingNewAssignment ? 100 : initialAssignment.pct);
    const [description, setDescription] = useState(
        creatingNewAssignment ? '' : initialAssignment.dsc === null ? '' : initialAssignment.dsc
    );
    const [alertModalContent, setAlertModalContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const setBegDate = (date) => {
        _setBegDate(date);
        if (date > endDate) {
            _setEndDate(date);
        }
    };
    const setEndDate = (date) => {
        _setEndDate(date);
        if (date < begDate) {
            _setBegDate(date);
        }
    };
    const getBegWeek = () => {
        return WeekHelper.GetWeek(begDate);
    };
    const getEndWeek = () => {
        return WeekHelper.GetWeek(endDate);
    };
    const getUpdatedAssignment = () => {
        return {
            dsc: description === '' ? null : description,
            bltID: bltID,
            begWek: getBegWeek(),
            endWek: getEndWeek(),
            pct: pct,
        };
    };

    // ------- Bools -------
    const hasEditingPermission = user.isAdmin;
    let isDisabled;
    let hasAsignmentStarted;
    let hasAssignmentEnded;
    let isDeleteOk;
    let isSaveOk;
    let isEmptyDescriptionAllowed;
    let isBlgDateNotLatest = selectedCoveringDate.getTime() !== latestCoveringDate.getTime();
    let isCreatingInThePast;
    if (creatingNewAssignment) {
        hasAsignmentStarted = false;
        hasAssignmentEnded = false;
        isEmptyDescriptionAllowed = !(bltID === 'UP');
        isCreatingInThePast = getBegWeek() < currentWeek;

        isDisabled = false;
        if (hasAssignmentEnded || !hasEditingPermission) {
            isDisabled = true;
        } else if (isBlgDateNotLatest) {
            isDisabled = true;
        }
        isSaveOk = true;
        if (!isEmptyDescriptionAllowed && description === '') {
            isSaveOk = false;
        } else if (isBlgDateNotLatest) {
            isSaveOk = false;
        } else if (isCreatingInThePast) {
            isSaveOk = false;
        }
        isDeleteOk = false;
    } else {
        if (bltID === 'UP' && (initialAssignment.bltID !== 'UP' || (initialAssignment.bltID === 'UP' && initialAssignment.dsc !== null))) {
            isEmptyDescriptionAllowed = false;
        } else {
            isEmptyDescriptionAllowed = true;
        }
        hasAsignmentStarted = initialAssignment.begWek < currentWeek;
        hasAssignmentEnded = initialAssignment.endWek < currentWeek;

        isDisabled = false;
        if (hasAssignmentEnded || !hasEditingPermission) {
            isDisabled = true;
        } else if (isBlgDateNotLatest) {
            isDisabled = true;
        }
        isSaveOk = true;
        const updatedAssignment = getUpdatedAssignment();
        if (assignmentsEquals(initialAssignment, updatedAssignment)) {
            isSaveOk = false;
        } else if (!isEmptyDescriptionAllowed && description === '') {
            isSaveOk = false;
        } else if (isBlgDateNotLatest) {
            isSaveOk = false;
        }
        isDeleteOk = true;
        if (hasAsignmentStarted) {
            isDeleteOk = false;
        }
    }

    // ------- Constraints: Selectable in dropdown -------
    const maxPctAvailable = calculateMaxPctAvailable(coveringRateWithoutInitialAssignment, getBegWeek(), getEndWeek());
    if (pct > maxPctAvailable) {
        setPct(maxPctAvailable);
    }
    const percents = [25, 50, 75, 100].filter((p) => p <= maxPctAvailable);

    const [minSelectableDate, maxSelectableDate] = calculateDateBoundaries(
        coveringRateWithoutInitialAssignment,
        currentWeek,
        getBegWeek(),
        getEndWeek()
    );

    // ------- Save handlers -------
    const handleSaveSuccess = () => {
        dispatch({ type: 'SHOW_SAVED_TOAST' });
        handleClose();
    };

    const handleDeleteSuccess = () => {
        dispatch({ type: 'SHOW_DELETED_TOAST' });
        handleClose();
    };

    const showErrorMessage = (message) => {
        setAlertModalContent(message);
        setIsAlertModalOpen(true);
    };

    const handleSave = () => {
        if (creatingNewAssignment) {
            submitCreateAssignment();
        } else {
            submitUpdateAssignment();
        }
    };

    const handleDelete = () => {
        submitDeleteAssignment();
    };

    const submitCreateAssignment = () => {
        setIsSaving(true);
        const url = 'api/covering/createAssignment/';

        const body = {
            empID,
            createAssignment: getUpdatedAssignment(),
            filterStartWeek,
            filterEndWeek,
            coveringDate: selectedCoveringDateString,
        };

        axios.post(url, body).then(
            (response) => {
                setIsSaving(false);
                if (response.data.success) {
                    handleSaveSuccess();
                    updateFromResult(response.data);
                } else {
                    showErrorMessage(response.data.message);
                }
            },
            (error) => {
                setIsSaving(false);
                showErrorMessage(
                    'Det gick inte att spara ändringen. Vänligen kontakta alltid@consid.se om problemet kvarstår efter att du uppdaterat sidan.'
                );
            }
        );
    };

    const submitUpdateAssignment = () => {
        setIsSaving(true);
        const url = 'api/covering/updateAssignment/';

        const body = {
            empID,
            initial: initialAssignment,
            updated: getUpdatedAssignment(),
            filterStartWeek,
            filterEndWeek,
            coveringDate: selectedCoveringDateString,
        };

        axios.post(url, body).then(
            (response) => {
                setIsSaving(false);
                if (response.data.success) {
                    handleSaveSuccess();
                    updateFromResult(response.data);
                } else {
                    showErrorMessage(response.data.message);
                }
            },
            (error) => {
                setIsSaving(false);
                showErrorMessage(
                    'Det gick inte att spara ändringen. Vänligen kontakta alltid@consid.se om problemet kvarstår efter att du uppdaterat sidan.'
                );
            }
        );
    };

    const submitDeleteAssignment = () => {
        setIsDeleting(true);
        const url = 'api/covering/deleteAssignment/';
        const body = {
            empID,
            deleteAssignment: initialAssignment,
            filterStartWeek,
            filterEndWeek,
            coveringDate: selectedCoveringDateString,
        };

        axios.post(url, body).then(
            (response) => {
                setIsDeleting(false);
                if (response.data.success) {
                    handleDeleteSuccess();
                    updateFromResult(response.data);
                } else {
                    showErrorMessage(response.data.message);
                }
            },
            (error) => {
                setIsDeleting(false);
                showErrorMessage(
                    'Det gick inte att spara ändringen. Vänligen kontakta alltid@consid.se om problemet kvarstår efter att du uppdaterat sidan'
                );
            }
        );
    };

    // ------- Update after post -------
    const updateFromResult = (result) => {
        let newCoverings = initialCoverings.map((cov) => {
            if (cov.empID === empID) {
                cov.assignments = result.assignments;
                cov.renderSumAssignments = result.renderSumAssignments;
                cov.coveringRate = result.coveringRate;
            }
            return cov;
        });
        dispatch({
            type: 'UPDATE_COVERINGS',
            coverings: newCoverings,
        });
    };

    // ------- Main component --------
    return (
        <div ref={dropdownRef} className='assignment-editor'>
            <form className='dropdown-menu form-group d-block shadow p-2 rounded'>
                <Header {...{ handleClose, empName }} />
                <div className='dropdown-divider' />
                <WeekPickers
                    {...{
                        begDate,
                        setBegDate,
                        endDate,
                        setEndDate,
                        minSelectableDate,
                        maxSelectableDate,
                        isDisabled,
                        hasAsignmentStarted,
                        isCreatingInThePast,
                    }}
                />
                <div className='form-row'>
                    <CoveringTypes {...{ bltID, setBltID, bltIDToNames, isDisabled: isDisabled || hasAsignmentStarted }} />
                    <Percents {...{ pct, setPct, percents, isDisabled: isDisabled || hasAsignmentStarted }} />
                </div>
                <Description
                    {...{
                        description,
                        setDescription,
                        isDisabled: isDisabled || hasAsignmentStarted,
                        isEmptyDescriptionAllowed,
                        creatingNewAssignment,
                    }}
                />
                <Submits
                    {...{
                        hasEditingPermission,
                        creatingNewAssignment,
                        isDeleteOk,
                        isSaveOk,
                        hasAssignmentEnded,
                        hasAsignmentStarted,
                        handleClose,
                        handleSave,
                        isSaving,
                        isDeleting,
                        isBlgDateNotLatest,
                        setIsDeleteModalOpen,
                    }}
                />
            </form>
            <div className='covering-modals'>
                <AlertModal show={isAlertModalOpen} onHide={() => setIsAlertModalOpen(false)} content={alertModalContent} />
                <DeleteModal show={isDeleteModalOpen} onHide={() => setIsDeleteModalOpen(false)} handleDelete={handleDelete} />
            </div>
        </div>
    );
};

export default AssignmentEditor;
